<script lang="ts">
  import { Input, Textarea, Select, Button } from '@aatmanova/ui';
  import { UploadCloud, Database, Image, Coins, FileText, Search } from '@lucide/svelte';

  interface Props {
    isFormOpen: boolean;
    formMode: 'create' | 'edit';
    formId: string;
    formSku: string;
    formSlug: string;
    formName: string;
    formDescription: string;
    formRetailPrice: number;
    formOfferPrice: number;
    formWholesalePrice: number | null;
    formCostPrice: number | null;
    formStock: number;
    formStatus: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
    formCategoryId: string;
    formSubcategoryId: string;
    formMediaUrl: string;
    uploadingFile: boolean;
    formUnit: string;
    formIsFinalPrepped: boolean;
    formIsBuyEnabled: boolean;
    formIsQuoteEnabled: boolean;
    formLowStockThreshold: number;
    formHsnCode: string;
    formTags: string;
    formCareGuide: string;
    formSourcingInfo: string;
    formSeoTitle: string;
    formSeoDescription: string;
    formSpecification: string;
    formMediaList: { url: string; altText?: string; sortOrder: number }[];
    categories: any[];
    client: any;
    saveProductMutationIsPending: boolean;
    handleFileUpload: (e: any) => void;
    handleSaveProduct: (e: SubmitEvent) => void;
  }

  let {
    isFormOpen = $bindable(),
    formMode,
    formId,
    formSku = $bindable(),
    formSlug = $bindable(),
    formName = $bindable(),
    formDescription = $bindable(),
    formRetailPrice = $bindable(),
    formOfferPrice = $bindable(),
    formWholesalePrice = $bindable(),
    formCostPrice = $bindable(),
    formStock = $bindable(),
    formStatus = $bindable(),
    formCategoryId = $bindable(),
    formSubcategoryId = $bindable(),
    formMediaUrl = $bindable(),
    uploadingFile = $bindable(),
    formUnit = $bindable(),
    formIsFinalPrepped = $bindable(),
    formIsBuyEnabled = $bindable(),
    formIsQuoteEnabled = $bindable(),
    formLowStockThreshold = $bindable(),
    formHsnCode = $bindable(),
    formTags = $bindable(),
    formCareGuide = $bindable(),
    formSourcingInfo = $bindable(),
    formSeoTitle = $bindable(),
    formSeoDescription = $bindable(),
    formSpecification = $bindable(),
    formMediaList = $bindable(),
    categories,
    client,
    saveProductMutationIsPending,
    handleFileUpload,
    handleSaveProduct
  }: Props = $props();

  let activeTab = $state<'details' | 'history'>('details');
  let auditLogs = $state<any[]>([]);
  let logsLoading = $state(false);
  let newImageUrl = $state('');

  let selectedCategory = $derived(categories.find(c => c.id === formCategoryId));
  let subcategories = $derived(selectedCategory?.subcategories || []);

  // Reset subcategory if category changes and current subcategory is not valid under new category
  $effect(() => {
    if (formCategoryId) {
      const isValid = subcategories.some(sub => sub.id === formSubcategoryId);
      if (!isValid && formSubcategoryId !== '') {
        formSubcategoryId = '';
      }
    }
  });

  // Fetch operational history logs when switching to history tab
  function fetchHistory() {
    if (formMode === 'edit' && formId) {
      logsLoading = true;
      client.getAuditLogs('Product', formId)
        .then((res: any) => {
          auditLogs = res?.items || [];
        })
        .catch((err: any) => {
          console.error('Failed to load audit logs:', err);
        })
        .finally(() => {
          logsLoading = false;
        });
    }
  }

  $effect(() => {
    if (activeTab === 'history' && isFormOpen) {
      fetchHistory();
    }
  });

  // Reset local tab when drawer opens/closes
  $effect(() => {
    if (isFormOpen) {
      activeTab = 'details';
    }
  });

  // Disable background scrolling when drawer is open
  $effect(() => {
    if (isFormOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  });

  function addImageByUrl() {
    if (newImageUrl.trim() === '') return;
    formMediaList = [
      ...formMediaList,
      { url: newImageUrl, altText: formName, sortOrder: formMediaList.length }
    ];
    // Set formMediaUrl to first image if empty
    if (!formMediaUrl) {
      formMediaUrl = newImageUrl;
    }
    newImageUrl = '';
  }

  function removeImage(index: number) {
    formMediaList = formMediaList.filter((_, idx) => idx !== index);
    if (formMediaList.length > 0) {
      formMediaUrl = formMediaList[0].url;
    } else {
      formMediaUrl = '';
    }
  }

  function getLevelBadge(level?: string) {
    const lvl = level ? level.toUpperCase() : 'INFO';
    switch (lvl) {
      case 'CRITICAL':
        return 'badge-error';
      case 'WARNING':
        return 'badge-warning';
      case 'SUCCESS':
        return 'badge-success';
      default:
        return 'badge-info';
    }
  }
</script>

{#if isFormOpen}
  <div class="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-end">
    <div class="w-full max-w-4xl bg-base-100 border-l border-base-300 h-full flex flex-col justify-between shadow-2xl overflow-hidden transition-all duration-300">
      
      <!-- Drawer Header -->
      <div class="p-6 border-b border-base-300 bg-base-200/50">
        <div class="flex items-center justify-between pb-3">
          <div>
            <h3 class="text-xl font-bold tracking-tight text-base-content flex items-center gap-2">
              <span class="w-2.5 h-2.5 rounded-full bg-accent animate-pulse"></span>
              {formMode === 'create' ? 'Create Spiritual Item' : 'Edit Item Configurations'}
            </h3>
            {#if formMode === 'edit'}
              <p class="text-xs text-base-content/60 font-semibold uppercase tracking-wider mt-1">SKU: {formSku}</p>
            {/if}
          </div>
          <button onclick={() => isFormOpen = false} class="btn btn-sm btn-circle btn-ghost text-base-content/75 hover:bg-base-300">✕</button>
        </div>

        <!-- Navigation Tabs -->
        <div class="tabs tabs-boxed bg-base-300/40 p-1 rounded-xl flex gap-1 mt-4">
          <button 
            type="button"
            onclick={() => activeTab = 'details'} 
            class="tab flex-1 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-200 {activeTab === 'details' ? 'tab-active bg-primary text-primary-content shadow-md' : 'text-base-content/60 hover:text-base-content'}"
          >
            Resource Details
          </button>
          <button 
            type="button"
            disabled={formMode === 'create'}
            onclick={() => activeTab = 'history'} 
            class="tab flex-1 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-200 {formMode === 'create' ? 'opacity-40 cursor-not-allowed' : ''} {activeTab === 'history' ? 'tab-active bg-primary text-primary-content shadow-md' : 'text-base-content/60 hover:text-base-content'}"
          >
            Operational History
          </button>
        </div>
      </div>

      <!-- Drawer Body -->
      <div class="flex-1 overflow-y-auto p-6 space-y-6">
        
        {#if activeTab === 'details'}
          <form id="productForm" onsubmit={handleSaveProduct} class="space-y-6">
            
            <!-- Card 1: Core Metadata -->
            <div class="card bg-base-200 border border-base-300 shadow-sm rounded-2xl p-5 space-y-4">
              <h4 class="text-xs font-extrabold uppercase tracking-widest text-primary flex items-center gap-1.5 pb-2 border-b border-base-300/50">
                <Database class="w-4 h-4" /> Core Metadata
              </h4>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="form-control w-full">
                  <label for="sku" class="label">
                    <span class="label-text text-xs font-semibold text-base-content/70">SKU Code</span>
                  </label>
                  <Input
                    type="text"
                    id="sku"
                    required
                    disabled={formMode === 'edit'}
                    bind:value={formSku}
                    placeholder="CRY-QRTZ-002"
                  />
                </div>

                <div class="form-control w-full">
                  <label for="hsnCode" class="label">
                    <span class="label-text text-xs font-semibold text-base-content/70">HSN/SAC Code</span>
                  </label>
                  <Input
                    type="text"
                    id="hsnCode"
                    bind:value={formHsnCode}
                    placeholder="9701 or 6802"
                  />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="form-control w-full">
                  <label for="name" class="label">
                    <span class="label-text text-xs font-semibold text-base-content/70">Product Name</span>
                  </label>
                  <Input
                    type="text"
                    id="name"
                    required
                    bind:value={formName}
                    placeholder="Clear Quartz Point"
                  />
                </div>

                <div class="form-control w-full">
                  <label for="slug" class="label">
                    <span class="label-text text-xs font-semibold text-base-content/70">Slug URL Pathway</span>
                  </label>
                  <Input
                    type="text"
                    id="slug"
                    required
                    bind:value={formSlug}
                    placeholder="clear-quartz-point"
                  />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="form-control w-full">
                  <label for="categoryId" class="label">
                    <span class="label-text text-xs font-semibold text-base-content/70">Category</span>
                  </label>
                  <Select
                    id="categoryId"
                    required
                    bind:value={formCategoryId}
                  >
                    {#if categories}
                      {#each categories as cat}
                        <option value={cat.id}>{cat.name}</option>
                      {/each}
                    {/if}
                  </Select>
                </div>

                <div class="form-control w-full">
                  <label for="subcategoryId" class="label">
                    <span class="label-text text-xs font-semibold text-base-content/70">Subcategory</span>
                  </label>
                  <Select
                    id="subcategoryId"
                    bind:value={formSubcategoryId}
                  >
                    <option value="">None</option>
                    {#each subcategories as sub}
                      <option value={sub.id}>{sub.name}</option>
                    {/each}
                  </Select>
                </div>
              </div>
            </div>

            <div class="card bg-base-200 border border-base-300 shadow-sm rounded-2xl p-5 space-y-4">
              <h4 class="text-xs font-extrabold uppercase tracking-widest text-primary flex items-center gap-1.5 pb-2 border-b border-base-300/50">
                <Image class="w-4 h-4" /> Visual Gallery ({formMediaList.length})
              </h4>
              
              <!-- Images List Grid -->
              {#if formMediaList.length > 0}
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {#each formMediaList as img, idx}
                    <div class="relative aspect-square rounded-xl overflow-hidden group border border-base-300 bg-base-300 flex items-center justify-center">
                      <img src={img.url} alt="Gallery item" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      <div class="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button 
                          type="button" 
                          onclick={() => removeImage(idx)} 
                          class="btn btn-circle btn-error btn-xs"
                          title="Remove Image"
                        >
                          ✕
                        </button>
                      </div>
                      <div class="absolute bottom-1 right-1 badge badge-xs badge-neutral text-[9px] font-bold">#{idx + 1}</div>
                    </div>
                  {/each}
                </div>
              {:else}
                <div class="p-6 rounded-xl border border-dashed border-base-300 text-center text-base-content/40 text-xs font-medium">
                  No images uploaded yet. Upload images or add via URL below.
                </div>
              {/if}
 
              <!-- Upload Controls -->
              <div class="p-4 bg-base-300/50 rounded-xl space-y-3">
                <div class="flex flex-col sm:flex-row items-center gap-3">
                  <div class="w-full sm:flex-1">
                    <Input
                      type="url"
                      bind:value={newImageUrl}
                      placeholder="Paste image URL here..."
                      size="sm"
                    />
                  </div>
                  <button 
                    type="button" 
                    onclick={addImageByUrl} 
                    class="btn btn-sm btn-primary w-full sm:w-auto px-4 font-bold"
                  >
                    Add URL
                  </button>
                </div>
 
                <div class="divider text-[10px] uppercase font-bold text-base-content/30 my-1">OR</div>
 
                <div class="flex items-center justify-center w-full">
                  <input
                    type="file"
                    accept="image/*"
                    id="drawerMediaFile"
                    onchange={handleFileUpload}
                    class="hidden"
                  />
                  <label
                    for="drawerMediaFile"
                    class="btn btn-sm btn-outline border-base-300 text-base-content hover:bg-base-200 hover:text-base-content w-full cursor-pointer flex items-center justify-center gap-2"
                  >
                    {#if uploadingFile}
                      <span class="loading loading-spinner loading-xs"></span> Uploading...
                    {:else}
                      <UploadCloud class="w-4 h-4 text-primary" /> Upload Image File
                    {/if}
                  </label>
                </div>
              </div>
            </div>

            <!-- Card 3: Financials & Inventory -->
            <div class="card bg-base-200 border border-base-300 shadow-sm rounded-2xl p-5 space-y-4">
              <h4 class="text-xs font-extrabold uppercase tracking-widest text-primary flex items-center gap-1.5 pb-2 border-b border-base-300/50">
                <Coins class="w-4 h-4" /> Financials & Inventory
              </h4>

              <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div class="form-control w-full">
                  <label for="retailPrice" class="label">
                    <span class="label-text text-xs font-semibold text-base-content/70">Retail Price (MRP)</span>
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    id="retailPrice"
                    required
                    bind:value={formRetailPrice}
                  />
                </div>
                
                <div class="form-control w-full">
                  <label for="offerPrice" class="label">
                    <span class="label-text text-xs font-semibold text-base-content/70">Offer Price (Sale)</span>
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    id="offerPrice"
                    required
                    bind:value={formOfferPrice}
                  />
                </div>

                <div class="form-control w-full">
                  <label for="wholesalePrice" class="label">
                    <span class="label-text text-xs font-semibold text-base-content/70">Wholesale Price</span>
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    id="wholesalePrice"
                    bind:value={formWholesalePrice}
                  />
                </div>

                <div class="form-control w-full">
                  <label for="costPrice" class="label">
                    <span class="label-text text-xs font-semibold text-base-content/70">Cost Price</span>
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    id="costPrice"
                    bind:value={formCostPrice}
                  />
                </div>
              </div>

              <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div class="form-control w-full">
                  <label for="stock" class="label">
                    <span class="label-text text-xs font-semibold text-base-content/70">Current Stock</span>
                  </label>
                  <Input
                    type="number"
                    id="stock"
                    required
                    bind:value={formStock}
                  />
                </div>

                <div class="form-control w-full">
                  <label for="lowStockThreshold" class="label">
                    <span class="label-text text-xs font-semibold text-base-content/70">Low Stock Limit</span>
                  </label>
                  <Input
                    type="number"
                    id="lowStockThreshold"
                    bind:value={formLowStockThreshold}
                  />
                </div>

                <div class="form-control w-full">
                  <label for="unit" class="label">
                    <span class="label-text text-xs font-semibold text-base-content/70">Measurement Unit</span>
                  </label>
                  <Input
                    type="text"
                    id="unit"
                    bind:value={formUnit}
                    placeholder="Piece"
                  />
                </div>

                <div class="form-control w-full">
                  <label for="status" class="label">
                    <span class="label-text text-xs font-semibold text-base-content/70">Status</span>
                  </label>
                  <Select
                    id="status"
                    bind:value={formStatus}
                  >
                    <option value="DRAFT">DRAFT</option>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="ARCHIVED">ARCHIVED</option>
                  </Select>
                </div>
              </div>

              <!-- Switches Row -->
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div class="flex items-center justify-between p-3 bg-base-300/40 rounded-xl">
                  <span class="text-xs font-bold text-base-content/80">Final Prepped</span>
                  <input type="checkbox" class="toggle toggle-primary toggle-sm" bind:checked={formIsFinalPrepped} />
                </div>
                <div class="flex items-center justify-between p-3 bg-base-300/40 rounded-xl">
                  <span class="text-xs font-bold text-base-content/80">Buy Button Enabled</span>
                  <input type="checkbox" class="toggle toggle-secondary toggle-sm" bind:checked={formIsBuyEnabled} />
                </div>
                <div class="flex items-center justify-between p-3 bg-base-300/40 rounded-xl">
                  <span class="text-xs font-bold text-base-content/80">Quote Button Enabled</span>
                  <input type="checkbox" class="toggle toggle-accent toggle-sm" bind:checked={formIsQuoteEnabled} />
                </div>
              </div>
            </div>

            <!-- Card 4: Product Description & Info -->
            <div class="card bg-base-200 border border-base-300 shadow-sm rounded-2xl p-5 space-y-4">
              <h4 class="text-xs font-extrabold uppercase tracking-widest text-primary flex items-center gap-1.5 pb-2 border-b border-base-300/50">
                <FileText class="w-4 h-4" /> Descriptions & Attributes
              </h4>

              <div class="form-control w-full">
                <label for="description" class="label">
                  <span class="label-text text-xs font-semibold text-base-content/70">Long Description</span>
                </label>
                <Textarea
                  id="description"
                  required
                  rows={3}
                  bind:value={formDescription}
                  placeholder="A premium, hand-carved quartz crystal..."
                />
              </div>

              <div class="form-control w-full">
                <label for="careGuide" class="label">
                  <span class="label-text text-xs font-semibold text-base-content/70">Care Guide Instructions</span>
                </label>
                <Textarea
                  id="careGuide"
                  rows={2}
                  bind:value={formCareGuide}
                  placeholder="Do not wash in hot water. Charge under full moon."
                />
              </div>

              <div class="form-control w-full">
                <label for="sourcingInfo" class="label">
                  <span class="label-text text-xs font-semibold text-base-content/70">Ethical Sourcing Info</span>
                </label>
                <Textarea
                  id="sourcingInfo"
                  rows={2}
                  bind:value={formSourcingInfo}
                  placeholder="Hand-mined ethically in Madagascar."
                />
              </div>

              <div class="form-control w-full">
                <label for="tags" class="label">
                  <span class="label-text text-xs font-semibold text-base-content/70">Search Tags</span>
                  <span class="label-text-alt text-[10px] text-base-content/50">Comma-separated values</span>
                </label>
                <Input
                  type="text"
                  id="tags"
                  bind:value={formTags}
                  placeholder="crystals, quartz, healing, meditation"
                />
              </div>

              <div class="form-control w-full">
                <label for="specification" class="label">
                  <span class="label-text text-xs font-semibold text-base-content/70">Product Specifications JSON</span>
                  <span class="label-text-alt text-[10px] text-base-content/50">Valid JSON Object</span>
                </label>
                <Textarea
                  id="specification"
                  rows={4}
                  class="font-mono text-xs"
                  bind:value={formSpecification}
                  placeholder={`{\n  "origin": "Brazil",\n  "weight": "250g"\n}`}
                />
              </div>
            </div>

            <!-- Card 5: Search Engine Optimization -->
            <div class="card bg-base-200 border border-base-300 shadow-sm rounded-2xl p-5 space-y-4">
              <h4 class="text-xs font-extrabold uppercase tracking-widest text-primary flex items-center gap-1.5 pb-2 border-b border-base-300/50">
                <Search class="w-4 h-4" /> Search Engine Optimization
              </h4>

              <div class="form-control w-full">
                <label for="seoTitle" class="label">
                  <span class="label-text text-xs font-semibold text-base-content/70">Meta Title Tag</span>
                </label>
                <Input
                  type="text"
                  id="seoTitle"
                  bind:value={formSeoTitle}
                  placeholder="Clear Quartz Point - Buy Ethical Crystals"
                />
              </div>

              <div class="form-control w-full">
                <label for="seoDescription" class="label">
                  <span class="label-text text-xs font-semibold text-base-content/70">Meta Description Tag</span>
                </label>
                <Textarea
                  id="seoDescription"
                  rows={2}
                  bind:value={formSeoDescription}
                  placeholder="Ethically sourced, high energy clear quartz points. Available for shipping worldwide."
                />
              </div>
            </div>

          </form>
        {:else if activeTab === 'history'}
          <!-- Operational Audit Logs Timeline -->
          <div class="space-y-4">
            <div class="flex items-center justify-between pb-3 border-b border-base-300">
              <h4 class="text-xs font-extrabold uppercase tracking-wider text-base-content/60">Operational Audit Trail</h4>
              <button 
                type="button" 
                onclick={fetchHistory} 
                class="btn btn-xs btn-outline btn-ghost gap-1.5"
                disabled={logsLoading}
              >
                {#if logsLoading}
                  <span class="loading loading-spinner loading-xs"></span>
                {:else}
                  <span>🔄</span>
                {/if}
                Refresh
              </button>
            </div>

            {#if logsLoading && auditLogs.length === 0}
              <div class="flex flex-col items-center justify-center p-12 space-y-2 opacity-55">
                <span class="loading loading-spinner loading-md text-primary"></span>
                <span class="text-xs font-bold uppercase tracking-wider">Syncing database log records...</span>
              </div>
            {:else if auditLogs.length === 0}
              <div class="flex flex-col items-center justify-center p-12 text-center text-base-content/40 space-y-2 border border-dashed border-base-300 rounded-2xl">
                <span class="text-2xl">🔍</span>
                <span class="text-xs font-bold uppercase tracking-wider">No Operational Activity Logged</span>
              </div>
            {:else}
              <div class="overflow-x-auto border border-base-300 rounded-xl bg-base-200/40">
                <table class="table table-sm table-zebra w-full">
                  <thead>
                    <tr class="bg-base-300/50 text-[10px] font-bold uppercase tracking-widest text-base-content/70">
                      <th>Timestamp</th>
                      <th>Actor</th>
                      <th>Event</th>
                      <th>Severity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each auditLogs as log}
                      <tr class="hover:bg-base-200 transition-colors">
                        <td class="whitespace-nowrap">
                          <div class="flex flex-col">
                            <span class="text-xs font-bold tabular-nums">
                              {new Date(log.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <span class="text-[9px] font-semibold text-base-content/40 uppercase tracking-wide">
                              {new Date(log.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div class="flex flex-col">
                            <span class="text-xs font-bold">{log.user?.name || 'System'}</span>
                            <span class="text-[9px] font-semibold text-base-content/40">{log.user?.email || 'SYSTEM'}</span>
                          </div>
                        </td>
                        <td>
                          <div class="flex flex-col">
                            <span class="text-xs font-bold text-base-content/80 uppercase">{log.action}</span>
                            {#if log.metadata}
                              <span class="text-[9px] text-base-content/50 max-w-xs truncate font-mono mt-0.5" title={JSON.stringify(log.metadata)}>
                                {JSON.stringify(log.metadata)}
                              </span>
                            {/if}
                          </div>
                        </td>
                        <td>
                          <span class="badge badge-sm font-bold text-[9px] tracking-wide uppercase {getLevelBadge(log.level)}">
                            {log.level || 'INFO'}
                          </span>
                        </td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            {/if}
          </div>
        {/if}

      </div>

      <!-- Drawer Footer -->
      <div class="p-6 border-t border-base-300 bg-base-200/50 flex items-center justify-end gap-3">
        <Button
          type="button"
          onclick={() => isFormOpen = false}
          variant="outline"
          size="sm"
        >
          Cancel
        </Button>
        {#if activeTab === 'details'}
          <Button
            type="submit"
            form="productForm"
            disabled={saveProductMutationIsPending}
            variant="primary"
            size="sm"
          >
            {#if saveProductMutationIsPending}
              Saving...
            {:else}
              Save Product Config
            {/if}
          </Button>
        {/if}
      </div>

    </div>
  </div>
{/if}
